import { createContext, useContext, useState, useCallback } from 'react';
import type { CounterpartyTeam, Counterparty } from '../types';

/* eslint-disable react-refresh/only-export-components -- context exports provider + hook */
export interface TransactionsFilters {
  search: string;
  memberFilter: string;
  typeFilter: string;
  dateRange: { from: Date | null; to: Date | null };
}

const defaultFilters: TransactionsFilters = {
  search: '',
  memberFilter: 'all',
  typeFilter: 'all',
  dateRange: { from: null, to: null },
};

type ContextValue = {
  filters: TransactionsFilters;
  setSearch: (v: string) => void;
  setMemberFilter: (v: string) => void;
  setTypeFilter: (v: string) => void;
  setDateRange: (range: { from: Date | null; to: Date | null }) => void;
  team: CounterpartyTeam | null;
  isResponsible: boolean;
  user: Counterparty | null;
};

const TransactionsFilterContext = createContext<ContextValue | null>(null);

function getDefaultDateRange() {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  return { from: yesterday, to: today };
}

export function TransactionsFilterProvider({ children, team, isResponsible = false, user = null }: { children: React.ReactNode; team: CounterpartyTeam | null; isResponsible?: boolean; user?: Counterparty | null }) {
  const [filters, setFilters] = useState<TransactionsFilters>({ ...defaultFilters, dateRange: getDefaultDateRange() });
  const setSearch = useCallback((v: string) => setFilters((f) => ({ ...f, search: v })), []);
  const setMemberFilter = useCallback((v: string) => setFilters((f) => ({ ...f, memberFilter: v })), []);
  const setTypeFilter = useCallback((v: string) => setFilters((f) => ({ ...f, typeFilter: v })), []);
  const setDateRange = useCallback((range: { from: Date | null; to: Date | null }) => setFilters((f) => ({ ...f, dateRange: range })), []);
  return (
    <TransactionsFilterContext.Provider value={{ filters, setSearch, setMemberFilter, setTypeFilter, setDateRange, team, isResponsible, user }}>
      {children}
    </TransactionsFilterContext.Provider>
  );
}

export function useTransactionsFilters() {
  const ctx = useContext(TransactionsFilterContext);
  return ctx;
}
